<template>
  <tabs-skeleton v-if="status === 'loading'" :tabs-number="tabsNumber" :classes="classes" />
  <div v-else class="szw-row szw-gap-1 szw-overflow-auto szw-relative hide-scrollbar" ref="tabsContainerRef">
    <!-- SINGLE TAB -->
    <div
      data-test="navigation-tab"
      :class="[getActiveTabClasses(tab), props.classes]"
      v-for="(tab, tabIndex) of adjustedTabsMemo"
      :key="tab.uuid"
      @click="event => changeActiveTab(tab.uuid, event, tabIndex)"
      :e2e-id="tab.id"
    >
      <sprite v-if="tab.icon" :sprite-id="tab.icon" class="szw-h-4 szw-w-4" />
      <span>{{ tab.title || tab.name }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount, onBeforeMount, inject, onMounted, nextTick } from 'vue'
import TabsSkeleton from './TabsSkeleton.vue'
import Sprite from '@/__core__/components/sprite/Sprite.vue'

/**
 * @typedef {{ title?: string, name?: string }} Tab
 * @type {{
 *  tabs: Array<Tab>,
 *  active: Tab
 * }}
 */
const props = defineProps({
  tabs: {
    type: Array,
    default: () => []
  },
  classes: {
    type: String,
    default: () => ''
  },
  active: {
    type: Number,
    default: 0
  },
  shouldSetDefaultActive: {
    type: Boolean,
    default: true
  },
  tabClickFn: {
    type: Function,
    default: () => void 0
  },
  tabsNumber: {
    type: Number,
    default: 10
  }
})

const emit = defineEmits(['tab-clicked'])

const status = inject('status')

/** @type {Ref<Tab & {uuid: string | number}>} */
const activeTab = ref(null)
/** @type {Ref<HTMLDivElement>} */
const tabsContainerRef = ref(null)

const adjustedTabsMemo = computed(() =>
  props.tabs.map((currentTab, index) => ({
    ...currentTab,
    uuid: `${currentTab.title || currentTab.name}-${index}`
  }))
)

/** @param {Tab} tab */
const isTabActive = tab => activeTab.value?.uuid === tab.uuid

/** @param {Tab} tab */
const getActiveTabClasses = tab => [
  'szw-p-2 szw-rounded szw-cursor-pointer szw-text-xs szw-text-nowrap szw-flex szw-gap-1 szw-items-center',
  !isTabActive(tab) ? 'szw-text-text-icon-primary szw-bg-no-color' : 'szw-bg-color-secondary szw-text-text-active-navigation'
]

/**
 * @param {string | number} uuid
 * @param {MouseEvent} event
 */
const changeActiveTab = (uuid, event, tabIndex) => {
  activeTab.value = adjustedTabsMemo.value.find(tab => tab.uuid === uuid)
  props.tabClickFn(activeTab.value)

  emit('tab-clicked', activeTab.value)

  const selectedElement = event.currentTarget.closest('div')
  const containerRect = tabsContainerRef.value.getBoundingClientRect()
  const tabRect = selectedElement.getBoundingClientRect()

  let scrollLeft = tabsContainerRef.value.scrollLeft

  const leftMargin = containerRect.left
  const tabLeftWithoutMargin = tabRect.left - leftMargin

  const nextTab = adjustedTabsMemo.value[tabIndex + 1]
  const previousTab = adjustedTabsMemo.value[tabIndex - 1]
  const nextTabRect = nextTab ? selectedElement.nextElementSibling.getBoundingClientRect() : { width: tabRect.width }
  const previousTabRect = previousTab ? selectedElement.previousElementSibling.getBoundingClientRect() : { width: 0 }

  // if current tab is not fully visible
  if (tabLeftWithoutMargin + tabRect.width > containerRect.width) {
    scrollLeft += tabLeftWithoutMargin + tabRect.width - containerRect.width
  } else if (tabRect.left - containerRect.left < 0) {
    scrollLeft -= Math.abs(tabRect.left - containerRect.left)
  }

  // when next is not visible enough to be clicked
  if (nextTabRect.left - leftMargin + nextTabRect.width / 2 >= containerRect.width) {
    scrollLeft += nextTabRect.width / 2
  }

  // when previous is not visible enough to be clicked
  if (previousTabRect.left - leftMargin + previousTabRect.width / 2 < 0) {
    scrollLeft -= previousTabRect.width / 2
  }

  tabsContainerRef.value.scrollTo({ left: scrollLeft, top: 0, behavior: 'smooth' })
}

const setupDefaultPosition = () => {
  if (!tabsContainerRef.value) {
    return
  }

  // /** @type {HTMLDivElement} */
  // const activeTabElement = tabsContainerRef.value.children[props.active]
  // activeTabElement.click()
}

const setActiveTab = () => {
  if (props.active) {
    activeTab.value = adjustedTabsMemo.value?.[props.active] || {}
  } else if (props.shouldSetDefaultActive) {
    activeTab.value = adjustedTabsMemo.value?.[0] || {}
  }
}

const unwatch = watch(
  () => props.tabs,
  () => {
    setActiveTab()
  }
)

const unwatchTabsContainer = watch(
  () => tabsContainerRef.value,
  async () => nextTick(async () => nextTick(setupDefaultPosition))
)

onBeforeMount(() => {
  setActiveTab()
})

onMounted(() => {
  setupDefaultPosition()
})

onBeforeUnmount(() => {
  unwatch()
  unwatchTabsContainer()
})
</script>
